/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
  addThrottledEvent,
  IModuleProps,
  INodeProps
} from '@msdyn365-commerce-modules/utilities';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
  IActionContext,
  IGridSettings,
  IImageSettings,
  ILinkData,
  isEmptyOrNullObject
} from '@msdyn365-commerce/core';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import * as React from 'react';
import {
  IMenuItemData,
  ISmweNavigationMenuData
} from './smwe-navigation-menu.data';
import {
  ICmsNavItemsData,
  ICmsRetailItemMenuData,
  IMenuData,
  ISmweNavigationMenuProps,
  ISubMenusData,
  navigationMenuSource
} from './smwe-navigation-menu.props.autogenerated';

export interface INavigationMenuViewProps
  extends ISmweNavigationMenuProps<ISmweNavigationMenuData> {
  className: string;
  menuItemData: IMenuItemData[];
  Navigation: IModuleProps;
  MenuList: INodeProps;
  ListItem: INodeProps;
  Link: INodeProps;
  Button: INodeProps;
  Span: INodeProps;
  ImageContainer: INodeProps;
  MobileDescriptionContainer: INodeProps;
  MobileBackButton: INodeProps;
  MobileDescriptionLabel: INodeProps;
  imageSettings: IImageSettings;
  gridSettings: IGridSettings;
  isMobileView: boolean;
}

interface ICategoryHierarchyClass extends CategoryHierarchy {
  ClassName?: string;
  Children: CategoryHierarchy[] | ICategoryHierarchyClass[];
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface INavigationMenuState {
  isMobile: boolean;
}

/**
 *
 * NavigationMenu component
 * @extends {React.PureComponent<ISmweNavigationMenuProps<ISmweNavigationMenuData>>}
 */
class NavigationMenu extends React.PureComponent<
  ISmweNavigationMenuProps<ISmweNavigationMenuData>,
  INavigationMenuState
  > {
  private static CMSItemIdInit: number = 1000;
  private static RSItemIdInit: number = 5000;
  private resizeThrottledEventHandler?: (event: Event) => void;
  private menuItemList: IMenuItemData[];
  private isRetail: boolean = true;
  private isCMS: boolean = true;
  @observable
  private viewport: GridSize;
  public static getFriendlyName(
    locale: string,
    nameTranslations?: TextValueTranslation[]
  ): string | undefined {
    let nameTranslation: TextValueTranslation | undefined;
    if (locale && nameTranslations && nameTranslations.length > 0) {
      nameTranslation = nameTranslations.find(
        item => item.Language!.toLowerCase() === locale.toLowerCase()
      );
    }

    return nameTranslation && nameTranslation.Text;
  }

  constructor(props: ISmweNavigationMenuProps<ISmweNavigationMenuData>) {
    super(props);
    this._updateViewport = this._updateViewport.bind(this);
    this.viewport =
      props.context.request &&
        props.context.request.device &&
        props.context.request.device.Type === 'Mobile'
        ? 'xs'
        : 'lg';
    this._setNavigationSource(
      this.props.config && this.props.config.navigationMenuSource
    );
    this.state = { isMobile: this.isMobile };
    this.menuItemList = [];
  }

  @computed get isMobile(): boolean {
    return this.viewport === 'xs';
  }

  public componentDidMount(): void {
    this.resizeThrottledEventHandler =
      window &&
      addThrottledEvent(
        window,
        'resize',
        this._updateViewport as EventListener
      );
    this._updateViewport();
  }

  public componentWillUnmount(): void {
    window &&
      window.removeEventListener(
        'resize',
        this.resizeThrottledEventHandler!,
        false
      );
  }

  public render(): JSX.Element | null {
    this.menuItemList = this._mapData();

    if (isEmptyOrNullObject(this.props.data)) {
      this.props.telemetry.error('Navigatiomenu returned null');
      return null;
    }

    const defaultImageSettings: IImageSettings = {
      viewports: {
        xs: { q: `w=224&h=300&m=6`, w: 0, h: 0 },
        xl: { q: `w=224&h=300&m=6`, w: 0, h: 0 }
      },
      lazyload: true
    };

    const viewProps = {
      ...this.props,
      ...this.state,
      imageSettings: defaultImageSettings,
      gridSettings: this.props.context.request.gridSettings,
      Navigation: {
        moduleProps: this.props,
        className: classnames(
          'ms-nav',
          this.props.config.className,
          this.state.isMobile ? 'mobile-vp navbar-collapse' : 'desktop-vp'
        ),
        tag: 'nav',
        'aria-label': this.props.resources.menuAriaLabel
      },
      MenuList: {
        className: 'ms-nav__list',
        tag: 'ul'
      },
      ListItem: {
        className: 'ms-nav__list__item',
        tag: 'li',
        role: 'menu'
      },
      Link: {
        tag: 'a',
        className: 'ms-nav__list__item__link'
      },
      Button: {
        className: 'ms-nav__list__item__button',
        tag: 'button',

        onClick: this.props
      },
      ImageContainer: {
        className: 'ms-nav__list__item__image'
      },
      Span: {
        className: 'ms-nav__list__item__span',
        tag: 'span'
      },
      MobileDescriptionContainer: {
        className: 'ms-nav__list__mobile__container'
      },
      MobileBackButton: {
        className: 'ms-nav__list__mobile__container__button',
        tag: 'button',
        'aria-label': this.props.resources.backButtonAriaLabel,
        onClick: this.props
      },
      MobileDescriptionLabel: {
        className: 'ms-nav__list__mobile__container__span',
        tag: 'span'
      },
      isMobileView: this.state.isMobile,
      menuItemData: this.menuItemList,
      className: classnames('ms-nav', this.props.config.className)
    };

    return this.props.renderView(viewProps) as React.ReactElement;
  }

  private _formatLinkData = (href: ILinkData | string | undefined): string => {
    if (href === undefined) {
        return '';
    }

    const path: string = (href as ILinkData).destinationUrl || (href as string);
    return (new URL(path, this.props.context.request.url.requestUrl)).href;
};

  private _mapData(): IMenuItemData[] {
    const { config } = this.props;
    let mergedData: IMenuItemData[] = [];

    // will contain the nav's from the retail server later
    let retailServerNavItems: CategoryHierarchy[] | undefined;

    const navigationmenu: ISmweNavigationMenuData = this.props.data;

    // if there are categories to be pulled from the retail server, check them
    // out and potentially pull the ones with children into the retailSeverNavItems object
    if (
      navigationmenu.categories.result &&
      navigationmenu.categories.result.length > 0
    ) {
      retailServerNavItems = navigationmenu.categories.result.filter(
        x => x.Children && x.Children.length > 0
      );

      const retailServerMainMenu = retailServerNavItems[0];
      const hiddenRetailMenuItems = this.props.config.hiddenRetailMenuItems;

      if (hiddenRetailMenuItems) {
        // If there are specified items to hide in the Retail Menu, create an array with those items all
        // lowercase, separated by commas, with surrounding spaces trimmed.
        const hiddenItemsArray = hiddenRetailMenuItems.toLowerCase().split(',').map(item => item.trim()) || [];

        // To only compare hidden items to originally fetched Retail server menu, we have to remove items
        // that have already been injected by the CMS.
        const fetchedRetailMenuItems = retailServerNavItems[0].Children.filter(navItem => navItem.RecordId > 0);

        // Filter out Retail Menu items that match the hidden items specified.
        retailServerMainMenu.Children = fetchedRetailMenuItems.filter(navItem => {
          const hiddenItem = hiddenItemsArray.find(item => item === navItem.Name?.toLowerCase());
          return !hiddenItem;
        });
      }

      // this is where we inject the CMSMenu into the retailMenu
      this._injectCmsMenu(retailServerMainMenu, this.props.config.cmsRetailItemMenu);

      // Note: Below code will render all the Retail server navigation
      // retailServerNavItems.map((item: CategoryHierarchy) => (
      //     mergedData.push(this._constructMenuItemData(item, undefined, true))
      // ));

      // Note: In order to display full retail menu, please commentout following line
      // and enable above code

      // **NOTE**
      // this will need to later be refactored if the above code is uncommented
      const url = this.props.config.topLvlCategory?.destinationUrl;
      if (url) {
        retailServerMainMenu.Url = url;
      }

      const overrideServerName = this.props.config.topLvlCategoryText !== undefined && this.props.config.topLvlCategoryText !== null && this.props.config.topLvlCategoryText !== '';
      mergedData.push(
        this._constructMenuItemData(
          retailServerMainMenu,
          undefined,
          this.isRetail,
          0,
          overrideServerName
        )
      );
    }

    if (this.isCMS && config.cmsNavItems && config.cmsNavItems.length > 0) {
      config.cmsNavItems.map((item: ICmsNavItemsData, idx: number) =>
        mergedData.push(
          this._constructMenuItemData(undefined, item, false, idx + 1)
        )
      );
    }

    if (config.sliceIndex && config.sliceIndex > 0) {
      mergedData = this._swapArrayPos(mergedData, 0, config.sliceIndex);
    }

    return mergedData;
  }

  /**
   * Injects the regular menu into the category menu
   *
   * @param categoryMenu category pulled from 365
   * @param cmsMenu regular cms menu
   */
  private _injectCmsMenu(
    categoryMenu: ICategoryHierarchyClass,
    cmsMenu: ICmsRetailItemMenuData | undefined
  ): ICategoryHierarchyClass {
    // ensure that the cmsMenu actually exists before attempting to add it
    if (cmsMenu && cmsMenu.menu) {
      // sort the menu in ascending order so we can insert them straight
      const cmsMenuSort = cmsMenu.menu.sort(
        (first, second) => first.sliceIndex! - second.sliceIndex!
      );

      // generates a "unique" id where they are below 0 so no conflicts
      // happen with real record ids
      let idGenerator = 0;

      // insert each item into the category menu
      cmsMenuSort.forEach(item => {
        // if its not in the menu already, add it
        if (!this._isInMenu(item, categoryMenu)) {
          idGenerator--;
          // we need to spoof the cmsMenu into a categoryMenu because
          // they are rendered in batches. The renderer for the CMS is
          // different than the renderer for the categoryMenu, therefore
          // we need to spoof it.
          categoryMenu.Children.splice(Math.max(0, item.sliceIndex! - 1), 0, {
            Name: item.linkText,
            ClassName: item.altStyle ? 'ms-nav__list__item-alt' : undefined,
            Url: item.linkUrl.destinationUrl,
            RecordId: idGenerator,
            Children: [],
            NameTranslations: [
              {
                Text: item.linkText,
                Language: 'en-us'
              }
            ]
          });
        }
      });
    }
    return categoryMenu;
  }

  /**
   * checks to ensure that the current menu item is in the current category.
   *
   * The reason why we need to check this is to ensure we don't duplicate the
   * addition of the menu multiple times.
   *
   * @param menu regular menu datapoint from the CMS
   * @param category category pulled from 365
   */
  private _isInMenu(menu: IMenuData, category: CategoryHierarchy): boolean {
    return !!category.Children.find(
      item => item.RecordId < 0 && item.Name === menu.linkText
    );
  }

  private _swapArrayPos(
    mergedData: IMenuItemData[],
    oldIndex: number,
    newIndex: number
  ): IMenuItemData[] {
    if (newIndex >= mergedData.length) {
      let k = newIndex - mergedData.length + 1;
      while (k--) {
        mergedData.push();
      }
    }
    mergedData.splice(newIndex, 0, mergedData.splice(oldIndex, 1)[0]);
    return mergedData;
  }

  private _constructMenuItemFromRetailItem(
    retailItem?: ICategoryHierarchyClass,
    overrideServerName:boolean = false
  ): IMenuItemData {
    const locale =
      this.props.context &&
        this.props.context.request &&
        this.props.context.request.locale
        ? this.props.context.request.locale
        : 'en-us';

      return {
        id: NavigationMenu.RSItemIdInit++,
        linkText: overrideServerName?this.props.config.topLvlCategoryText || '':
          NavigationMenu.getFriendlyName(
            locale,
            retailItem && retailItem.NameTranslations
          ) || '',
        linkURL: retailItem && this._formatLinkData(retailItem.Url),
        className: retailItem?.ClassName,
        subMenu: this._getNestedChildren(retailItem)
      } as IMenuItemData;
  }

  private _constructMenuItemData(
    retailItem?: ICategoryHierarchyClass,
    cmsItem?: ICmsNavItemsData,
    isRetail?: boolean,
    idx?: number,
    overrideServerName:boolean = false
  ): IMenuItemData {
    if (isRetail) {
      return this._constructMenuItemFromRetailItem(retailItem, overrideServerName);
    } else {
      return {
        id: NavigationMenu.CMSItemIdInit++,
        linkText: (cmsItem && cmsItem.linkText) || '',
        linkURL:
          (cmsItem && cmsItem.linkUrl && this._formatLinkData(cmsItem.linkUrl.destinationUrl)) || '',
        imageDestinationURL:
          (cmsItem && cmsItem.imageLink && this._formatLinkData(cmsItem.imageLink.destinationUrl)) ||
          '',
        imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
        subMenu: this._getCMSNestedChildren(cmsItem)
      } as IMenuItemData;
    }
  }

  private _getNestedChildren(category?: CategoryHierarchy): IMenuItemData[] {
    const nestedChildren: IMenuItemData[] = [];
    if (!category || !category.Children || !category.Children.length) {
      return [];
    }
    if(!this.props.config.hideRetailAllMenu) {
      nestedChildren.push({
        linkText: this.props.resources.allCategoryMenuText,
        linkURL: this._formatLinkData(getCategoryUrl(
          category,
          (this.props.context as unknown) as IActionContext,
          {}
        ))
      });
    }
    category.Children.map((item: CategoryHierarchy) => {
      nestedChildren.push(this._constructMenuItemData(item, undefined, true));
    });
    return nestedChildren;
  }

  private _getCMSNestedChildren(category?: ICmsNavItemsData): IMenuItemData[] {
    const nestedChildren: IMenuItemData[] = [];

    if (!category || !category.subMenus || !category.subMenus.length) {
      return [];
    }

    category.subMenus.map((item: ISubMenusData, idx: number) => {
      nestedChildren.push(
        this._constructMenuItemData(undefined, item, false, idx)
      );
    });
    return nestedChildren;
  }

  private _setNavigationSource = (sourceType?: navigationMenuSource): void => {
    switch (sourceType) {
      case navigationMenuSource.retailServerOnly:
        this.isRetail = true;
        this.isCMS = false;
        break;
      case navigationMenuSource.cmsOnly:
        this.isRetail = false;
        this.isCMS = true;
        break;
      case navigationMenuSource.all:
        this.isRetail = true;
        this.isCMS = true;
        break;
      default:
    }
  };

  private _updateViewport(): void {
    this.viewport = this._getViewport();
    this.setState({ isMobile: this.isMobile });
  }

  private _getViewport(): GridSize {
    const { context } = this.props;

    // always render in mobile viewport on a mobile device
    if (
      context.request &&
      context.request.device &&
      context.request.device.Type === 'Mobile'
    ) {
      return 'xs';
    }

    if (typeof window !== undefined && window.innerWidth) {
      const gridSettings = context.request.gridSettings;
      if (gridSettings) {
        if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
          return 'xs';
        } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
          return 'xs';
        } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
          return 'md';
        } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
          return 'lg';
        } else {
          return 'xl';
        }
      }
    }

    return 'xs';
  }
}

export default NavigationMenu;
